import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ROUTES } from './config/routes';
import AppLayout from './layout/AppLayout';
import PrivateRoutes from './privateRoutes/PrivateRoutes';
import { fetchMeThunk } from './redux/slices/authSlice';
import { useAppDispatch, useAppSelector } from './redux/store';
import AdminMachinesView from './views/admin/AdminMachinesView';
import AdminSensorsView from './views/admin/AdminSensorsView';
import AdminUsersView from './views/admin/AdminUsersView';
import AlarmsView from './views/alarms/AlarmsView';
import DashboardView from './views/dashboard/DashboardView';
import DiagnosticsView from './views/diagnostics/DiagnosticsView';
import ForgotPasswordView from './views/forgotPassword/ForgotPasswordView';
import InsightsView from './views/insights/InsightsView';
import LifterDashboardView from './views/lifter/LifterDashboardView';
import LoginView from './views/login/LoginView';
import ProfileView from './views/profile/ProfileView';
import RegisterView from './views/register/RegisterView';
import ReportsView from './views/reports/ReportsView';
import ResetPasswordView from './views/resetPassword/ResetPasswordView';
import SegmentorView from './views/segmentor/SegmentorView';
import VerifyAccountView from './views/verifyAccount/VerifyAccountView';

const App = () => {
  const dispatch = useAppDispatch();
  const status = useAppSelector((s) => s.auth.status);

  useEffect(() => {
    if (status === 'idle') {
      void dispatch(fetchMeThunk());
    }
  }, [status, dispatch]);

  const wrap = (node: React.ReactNode) => (
    <PrivateRoutes>
      <AppLayout>{node}</AppLayout>
    </PrivateRoutes>
  );

  const wrapStaff = (node: React.ReactNode) => (
    <PrivateRoutes permission={{ module: 'admin', action: 'admin' }}>
      <AppLayout>{node}</AppLayout>
    </PrivateRoutes>
  );

  return (
    <Routes>
      {/* public */}
      <Route path={ROUTES.LOGIN} element={<LoginView />} />
      <Route path={ROUTES.REGISTER} element={<RegisterView />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordView />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordView />} />
      <Route path={ROUTES.VERIFY_ACCOUNT} element={<VerifyAccountView />} />

      {/* private */}
      <Route path={ROUTES.DASHBOARD} element={wrap(<DashboardView />)} />
      <Route path={ROUTES.INSIGHTS} element={wrap(<InsightsView />)} />
      <Route path={ROUTES.DIAGNOSTICS} element={wrap(<DiagnosticsView />)} />
      <Route path={ROUTES.LIFTER_DASHBOARD} element={wrap(<LifterDashboardView />)} />
      <Route path={ROUTES.ALARMS} element={wrap(<AlarmsView />)} />
      <Route path={ROUTES.REPORTS} element={wrap(<ReportsView />)} />
      <Route path={ROUTES.SEGMENTOR} element={wrap(<SegmentorView />)} />
      <Route path={ROUTES.PROFILE} element={wrap(<ProfileView />)} />

      {/* admin (staff-only) */}
      <Route path={ROUTES.ADMIN_USERS} element={wrapStaff(<AdminUsersView />)} />
      <Route path={ROUTES.ADMIN_MACHINES} element={wrapStaff(<AdminMachinesView />)} />
      <Route path={ROUTES.ADMIN_SENSORS} element={wrapStaff(<AdminSensorsView />)} />

      <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
    </Routes>
  );
};

export default App;
